import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Formik, Form, FormikErrors, FormikValues } from "formik";
import BasicDetailsStep from "./basic-details/basic-details";
import SubscriptionPlans from "./subscriptions-plans/subscription-plans";
import AddUser from "./add-user/add-user";
import MuiStepper from "../../../common/components/mui-stepper/mui-stepper";
import RightArrow from "../../../assets/icons/right-arrow.svg";
import "./add-new-tenant.scss";
import {
  CompanyFormValues,
  filterSubscriptionPlans,
  getInitialValues,
  getUserErrorMessage,
  markAllFieldsTouched,
  validationSchema,
} from "./add-new-tenet-helper";
import {
  createTenet,
  getSubscriptionPlans,
  getTenetRoles,
  uploadTenetLogo,
} from "../../../api/tenets";
import { useSetLoading } from "../../../common/hooks/appearance";
import { AlertComponent } from "../../../common/components";
import { ROLES } from "../../../utils/constants";

interface AddNewTenantProps {
  open: boolean;
  onClose: () => void;
  setAlert: (alert: any) => void;
  rowData?: any;
}

interface AlertMessage {
  open: boolean;
  message?: any;
  type?: "success" | "error" | "info" | "warning";
  onClose?: () => void;
}

const steps: string[] = ["Basic Details", "Subscription Plans", "Add Users"];
const nextButtonLabel = { 0: "Choose Plan", 1: "Add User", 2: "Submit" };

const AddNewTenant: React.FC<AddNewTenantProps> = ({
  open,
  onClose,
  setAlert,
  rowData,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [checked, setChecked] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [tenetRoles, setTenetRoles] = useState<any[]>([]);
  const [isExistCompanyName, setIsExistCompanyName] = useState<boolean>(false);
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);

  const [alertMessage, setAlertMessage] = useState<AlertMessage>({
    open: false,
  });

  const modalBodyRef = useRef<HTMLDivElement | null>(null);
  const initialValues = getInitialValues(rowData);
  const handleNext = async (
    values: any,
    setTouched: any,
    validateForm: any,
    handleSubmit: any
  ) => {
    setSubmitAttempted(true);
    const touched = markAllFieldsTouched(initialValues);
    setTouched(touched);
    const errors = await validateForm();

    if (activeStep === 1 && !values?.subscriptionPlanId?.id) {
      setAlertMessage({
        open: true,
        message: "Please choose a subscription plan to proceed!",
        type: "error",
      });
      return;
    }

    if (
      activeStep === 2 &&
      values?.users?.length > values?.subscriptionPlanId?.no_of_users &&
      values?.subscriptionPlanId?.no_of_users !== -1
    ) {
      setAlertMessage({
        open: true,
        message: "You've exceeded your plan's user limit. Upgrade your plan.!",
        type: "error",
      });
      return;
    }

    const noErrors = Object.keys(errors).length === 0;
    const { users: _, ...rest } = errors || {};
    const isPrimaryUserValid =
      !errors?.users?.[0] && Object.keys(rest).length === 0;

    if (activeStep === 2 && !noErrors && errors?.users?.length) {
      const errMessage: any = getUserErrorMessage(
        errors.users,
        values.users,
        5,
        true
      );
      if (errMessage) {
        setAlertMessage({
          open: true,
          message: (
            <Typography sx={{ fontSize: "13px" }}>
              {!errMessage.includes("almost there!") ? (
                <>
                  You&apos;re almost there! Please review the errors below.{" "}
                  <br />
                </>
              ) : (
                ""
              )}
              {errMessage}
            </Typography>
          ),
          type: "error",
        });
        return;
      }
    }
    if (noErrors && activeStep < 2) {
      setActiveStep((prevStep) => prevStep + 1);
    } else if (activeStep === 0 && isPrimaryUserValid) {
      setActiveStep((prevStep) => prevStep + 1);
    } else if (activeStep === 2) {
      if (noErrors) {
        handleSubmit();
      } else {
        setAlertMessage({
          open: true,
          message:
            "It looks like some information is missing. Please review all steps carefully!",
          type: "error",
        });
      }
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      onClose();
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const getStepContent = (
    step: number,
    values: CompanyFormValues,
    errors: FormikErrors<FormikValues>,
    handleChange: any,
    touched: any
  ) => {
    switch (step) {
      case 0:
        return (
          <BasicDetailsStep
            values={values}
            errors={errors}
            handleChange={handleChange}
            touched={touched}
            checked={checked}
            setChecked={setChecked}
            setIsExistCompanyName={setIsExistCompanyName}
            isExistCompanyName={isExistCompanyName}
            submitAttempted={submitAttempted}
          />
        );
      case 1:
        return (
          <SubscriptionPlans
            values={values}
            errors={errors}
            subscriptionData={subscriptionData}
            getSubscriptionValues={getSubscriptionValues}
          />
        );
      case 2:
        return (
          <AddUser values={values} errors={errors} tenetRoles={tenetRoles} />
        );
      default:
        return null;
    }
  };

  const handleStep = async (
    step: number,
    values: any,
    validateForm: any,
    setTouched: any
  ) => {
    const touched = markAllFieldsTouched(initialValues);
    setTouched(touched);
    const errors = await validateForm();

    const isValid = Object.keys(errors).length === 0;
    const hasSubscription = !!values?.subscriptionPlanId?.id;
    const isMovingForward = step > activeStep;

    if (step === 2 && !hasSubscription) {
      setAlertMessage({
        open: true,
        message:
          activeStep === 1
            ? "Please choose a subscription plan to proceed!"
            : `Please fill in all the step details before proceeding to the "Add User" tab!`,
        type: "error",
      });
      return;
    }

    if (activeStep === 1 && isMovingForward && !hasSubscription) {
      setAlertMessage({
        open: true,
        message: "Please choose a subscription plan to proceed!",
        type: "error",
      });
      return;
    }

    const { users: _, ...rest } = errors || {};
    const isPrimaryUserValid =
      !errors?.users?.[0] && Object.keys(rest).length === 0;

    if (isPrimaryUserValid && activeStep === 0) {
      setActiveStep(step);
    } else if (isValid && (hasSubscription || step === activeStep + 1)) {
      setActiveStep(step);
    } else if (step < activeStep) {
      setActiveStep(step);
    }
  };

  const addTenet = async (payload: any, values: any) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", values?.logo_url);
      formData.append("companyName", values?.companyName);

      const imgRes = await uploadTenetLogo(formData);
      if (imgRes?.data?.data?.logo_url) {
        const response: any = await createTenet({
          ...payload,
          ...imgRes?.data?.data,
        });

        if (response?.data?.success) {
          setIsLoading(false);
          setAlert({
            isOpen: true,
            message: "Partner successfully onboarded.",
            type: "success",
          });
          setTimeout(() => {
            onClose();
          }, 300);
        }
      }
    } catch (e: any) {
      setIsLoading(false);
      setAlert({
        isOpen: true,
        message:
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Some thing went wrong!",
        type: "error",
      });
    }
  };
  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const getSubscriptionValues = async () => {
    try {
      setIsLoading(true);
      const response: any = await getSubscriptionPlans();
      const { monthly, annually, noCost, customMonthly, customAnnual } =
        filterSubscriptionPlans(response?.data?.data);
      setIsLoading(false);

      setSubscriptionData({
        monthly: monthly,
        annually: annually,
        noCost: noCost,
        customMonthly,
        customAnnual,
      });
    } catch (e: any) {
      setIsLoading(false);
      setAlertMessage({
        open: true,
        message:
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Some thing went wrong!",
        type: "error",
      });
    }
  };

  const getRole = async () => {
    try {
      setIsLoading(true);
      const response: any = await getTenetRoles();

      if (response?.data?.success) {
        const roleOptions = response?.data?.data?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.role,
            disabled: item?.role === ROLES.SUPER_ADMIN,
          };
        });

        setTenetRoles(roleOptions);
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      setAlert({
        isOpen: true,
        message: error?.response?.data?.message || "some thing went wrong!",
        type: "error",
      });
    }
  };

  useEffect(() => {
    getSubscriptionValues();
    getRole();
  }, []);

  useEffect(() => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTo(0, 0);
    }
  }, [activeStep]);

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Partner</h3>
        </div>
        <div className="modal-body" ref={modalBodyRef}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema[activeStep]}
            validateOnBlur
            onSubmit={(values) => {
              const temp: any = { ...values };
              temp.subscriptionPlanId = values?.subscriptionPlanId?.id;
              temp.timezone = values?.timezone || "";
              temp.users = values?.users;
              temp.planCategory = values?.subscriptionPlanId?.plan_category
                ?.toLowerCase()
                ?.split("_")
                ?.join(" ");
              temp.planType = values?.subscriptionPlanId?.plan_type;
              if (values?.subscriptionPlanId?.plan_category !== "TRIAL") {
                temp.planType =
                  values?.subscriptionPlanId?.plan_type?.toLowerCase();
              }
              addTenet(temp, values);
            }}
          >
            {({
              handleSubmit,
              values,
              errors,
              handleChange,
              validateForm,
              setTouched,
              touched,
            }) => (
              <Form>
                <Box className="add-new-tenant-wrapper">
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <MuiStepper
                      steps={steps}
                      activeStep={activeStep}
                      handleStep={(index) => {
                        if (!isExistCompanyName) {
                          handleStep(index, values, validateForm, setTouched);
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ minHeight: "400px" }}>
                    {getStepContent(
                      activeStep,
                      values,
                      errors,
                      handleChange,
                      touched
                    )}
                  </Box>
                </Box>
                <Box className="modal-footer">
                  {activeStep > 0 && (
                    <button
                      onClick={onClose}
                      className="cancel-button"
                      type="button"
                    >
                      Cancel
                    </button>
                  )}
                  <Box sx={{ flex: "1 1 auto" }} />

                  <button
                    type="button"
                    onClick={handleBack}
                    className="cancel-button"
                  >
                    {activeStep === 0 ? "Cancel" : "Previous"}
                  </button>

                  <button
                    onClick={() =>
                      handleNext(values, setTouched, validateForm, handleSubmit)
                    }
                    className="cancel-button next-button-wrapper"
                    disabled={isExistCompanyName}
                    type="button"
                  >
                    {
                      nextButtonLabel?.[
                        activeStep as keyof typeof nextButtonLabel
                      ]
                    }{" "}
                    {activeStep !== 2 && (
                      <img src={RightArrow} alt="right-arrow" />
                    )}
                  </button>
                </Box>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      {alertMessage?.open && (
        <AlertComponent
          message={alertMessage?.message}
          type="error"
          open={alertMessage?.open}
          onClose={() => {
            setAlertMessage({ open: false });
          }}
        />
      )}
    </div>
  );
};

export default AddNewTenant;
